import { Vector3 } from '@slimebound/core';
import type { ArcRotateCamera } from '@babylonjs/core';

export interface CameraControllerOptions {
  initialRadius?: number;
  minRadius?: number;
  maxRadius?: number;
  damping?: number;
  initialAlpha?: number;
  initialBeta?: number;
  minBeta?: number;
  maxBeta?: number;
}

export class CameraController {
  public radius: number;
  public targetRadius: number;
  public minRadius: number;
  public maxRadius: number;
  public damping: number;

  public alpha: number;
  public beta: number;
  public minBeta: number;
  public maxBeta: number;

  public target: Vector3;
  public desiredTarget: Vector3;

  private babylonCamera: ArcRotateCamera | null = null;

  constructor(options: CameraControllerOptions = {}) {
    this.minRadius = options.minRadius ?? 4;
    this.maxRadius = options.maxRadius ?? 50;
    this.radius = options.initialRadius ?? 15;
    this.targetRadius = this.radius;
    this.damping = options.damping ?? 8;

    this.alpha = options.initialAlpha ?? -Math.PI / 2;
    this.beta = options.initialBeta ?? Math.PI / 3;
    this.minBeta = options.minBeta ?? 0.1;
    this.maxBeta = options.maxBeta ?? Math.PI / 2 - 0.05;

    this.target = new Vector3(0, 0, 0);
    this.desiredTarget = new Vector3(0, 0, 0);
  }

  public attachBabylonCamera(camera: ArcRotateCamera): void {
    this.babylonCamera = camera;
    this.babylonCamera.lowerRadiusLimit = this.minRadius;
    this.babylonCamera.upperRadiusLimit = this.maxRadius;
    this.babylonCamera.lowerBetaLimit = this.minBeta;
    this.babylonCamera.upperBetaLimit = this.maxBeta;
  }

  public zoom(delta: number): void {
    this.targetRadius = Math.max(this.minRadius, Math.min(this.maxRadius, this.targetRadius + delta));
  }

  public setTarget(x: number, y: number, z: number): void {
    this.desiredTarget = new Vector3(x, y, z);
  }

  public update(dt: number): void {
    const t = 1 - Math.exp(-this.damping * dt);

    // Smoothly interpolate radius
    this.radius += (this.targetRadius - this.radius) * t;

    // Smoothly interpolate target position
    this.target = this.target.lerp(this.desiredTarget, t);

    // Synchronize to Babylon camera if attached
    if (this.babylonCamera) {
      this.babylonCamera.radius = this.radius;
      this.babylonCamera.target.x = this.target.x;
      this.babylonCamera.target.y = this.target.y;
      this.babylonCamera.target.z = this.target.z;
    }
  }

  public setShakeOffset(offset: { x: number; y: number; z: number }): void {
    if (this.babylonCamera) {
      this.babylonCamera.target.x = this.target.x + offset.x;
      this.babylonCamera.target.y = this.target.y + offset.y;
      this.babylonCamera.target.z = this.target.z + offset.z;
    }
  }
}
